import { memo, useEffect, useMemo, useState } from 'react'
import { MetricsWidgetProps } from '@/api/Metrics/Types/MetricsDto'
import { ComparisonChart } from '@/components/Chart/ComparisonChart'
import { Loading } from '@/components/Loading'
import {
  ChartWidgetType,
  FTD_METRICS_CATEGORY,
  NEW_CLIENTS_METRICS_CATEGORY,
  OPENED_TA_METRICS_CATEGORY,
  WIDGET_CONFIG,
} from '@/components/Metrics/config'
import { processComparisonChartData, TData } from '@/components/Metrics/helpers/processChartData'
import { EmptyStateSkeleton } from '@/components/ui/empty-state-skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useApiClient } from '@/utils/api/ApiClient'
import { MetricsApiClient } from '@/utils/api/metricsApi'
import { API_DATE_FORMAT, LAST_30_DAYS_INCLUDING_TODAY } from '@/utils/date'
import { format } from 'date-fns'

const availableWidgets = Object.keys(WIDGET_CONFIG) as ChartWidgetType[]

export const ComparisonMetricsWidget = memo(
  ({ introducingBrokerIds, countryIds, customDateRange, intervalType }: MetricsWidgetProps) => {
    const metricsApiClient = useApiClient(MetricsApiClient)

    const [primaryWidgetType, setPrimaryWidgetType] = useState<ChartWidgetType | null>(null)
    const [secondaryWidgetType, setSecondaryWidgetType] = useState<ChartWidgetType | null>(null)

    const [primaryData, setPrimaryData] = useState<TData | null>(null)
    const [secondaryData, setSecondaryData] = useState<TData | null>(null)

    const [isPrimaryLoading, setIsPrimaryLoading] = useState(false)
    const [isSecondaryLoading, setIsSecondaryLoading] = useState(false)
    const isLoading = isPrimaryLoading || isSecondaryLoading

    const [dateRange, setDateRange] = useState<[Date, Date]>(() =>
      customDateRange
        ? [new Date(customDateRange[0]), new Date(customDateRange[1])]
        : LAST_30_DAYS_INCLUDING_TODAY
    )

    const from = format(dateRange[0], API_DATE_FORMAT)
    const to = format(dateRange[1], API_DATE_FORMAT)

    const primaryWidgetConfig = useMemo(
      () => (primaryWidgetType ? WIDGET_CONFIG[primaryWidgetType] : null),
      [primaryWidgetType]
    )
    const secondaryWidgetConfig = useMemo(
      () => (secondaryWidgetType ? WIDGET_CONFIG[secondaryWidgetType] : null),
      [secondaryWidgetType]
    )

    const comparisonData = useMemo(() => {
      return processComparisonChartData(
        primaryData,
        secondaryData,
        primaryWidgetConfig?.category,
        secondaryWidgetConfig?.category
      )
    }, [primaryData, secondaryData, primaryWidgetConfig, secondaryWidgetConfig])

    useEffect(() => {
      if (!primaryWidgetConfig) {
        return
      }

      const fetchPrimaryData = async () => {
        setIsSecondaryLoading(true)

        try {
          const data = await primaryWidgetConfig.fetchData(metricsApiClient, {
            from,
            to,
            introducingBrokerIds,
            countryIds,
            intervalType,
          })

          setPrimaryData(data)
        } catch (e) {
          console.error(e)
        } finally {
          setIsSecondaryLoading(false)
        }
      }

      fetchPrimaryData()
    }, [primaryWidgetConfig, from, to, introducingBrokerIds, countryIds, intervalType])

    useEffect(() => {
      if (!secondaryWidgetConfig) {
        return
      }

      const fetchSecondaryData = async () => {
        setIsPrimaryLoading(true)

        try {
          const data = await secondaryWidgetConfig.fetchData(metricsApiClient, {
            from,
            to,
            introducingBrokerIds,
            countryIds,
            intervalType,
          })

          setSecondaryData(data)
        } catch (e) {
          console.error(e)
        } finally {
          setIsPrimaryLoading(false)
        }
      }

      fetchSecondaryData()
    }, [secondaryWidgetConfig, from, to, introducingBrokerIds, countryIds, intervalType])

    useEffect(() => {
      if (customDateRange) {
        setDateRange([new Date(customDateRange[0]), new Date(customDateRange[1])])
      } else {
        setDateRange(LAST_30_DAYS_INCLUDING_TODAY)
      }
    }, [customDateRange])

    const isIntegerMetric =
      primaryWidgetConfig?.category === NEW_CLIENTS_METRICS_CATEGORY ||
      primaryWidgetConfig?.category === OPENED_TA_METRICS_CATEGORY ||
      primaryWidgetConfig?.category === FTD_METRICS_CATEGORY

    const decimalPlaces = isIntegerMetric ? 0 : 2

    return (
      <>
        <div className='flex flex-col gap-2 p-3 pb-0'>
          <div className='text-[1.125rem] font-semibold text-muted-foreground'>
            Metrics comparison
          </div>
          <div className='flex flex-wrap items-center gap-4'>
            <div className='flex-1 min-w-25'>
              <Select
                value={primaryWidgetType ?? undefined}
                onValueChange={(val) => {
                  setPrimaryWidgetType(val as ChartWidgetType)
                }}
              >
                <SelectTrigger className='w-full' size='sm'>
                  <SelectValue className='text-primary' placeholder='Select primary metric'>
                    {primaryWidgetType && WIDGET_CONFIG[primaryWidgetType]?.title}
                  </SelectValue>
                  <div className='w-2.5 h-2.5 bg-[#82ca9d] rounded-full' />
                </SelectTrigger>
                <SelectContent>
                  {availableWidgets
                    .filter((widget) => widget !== secondaryWidgetType)
                    .map((widget) => (
                      <SelectItem key={widget} value={widget}>
                        {WIDGET_CONFIG[widget]?.title}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className='shrink-0 whitespace-nowrap'>vs</div>
            <div className='flex-1 min-w-25'>
              <Select
                value={secondaryWidgetType ?? undefined}
                onValueChange={(val) => setSecondaryWidgetType(val as ChartWidgetType)}
                disabled={!primaryWidgetType}
              >
                <SelectTrigger className='w-full' size='sm'>
                  <SelectValue className='text-primary' placeholder='Select secondary metric'>
                    {secondaryWidgetType && WIDGET_CONFIG[secondaryWidgetType]?.title}
                  </SelectValue>
                  <div className='w-2.5 h-2.5 bg-[#8884d8] rounded-full' />
                </SelectTrigger>
                <SelectContent>
                  {availableWidgets
                    .filter((widget) => widget !== primaryWidgetType)
                    .map((widget) => (
                      <SelectItem key={widget} value={widget}>
                        {WIDGET_CONFIG[widget]?.title}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <Loading isLoading={isLoading} modalView>
          {primaryData ? (
            <div className='flex flex-col h-full min-h-0 p-3 rounded-lg'>
              <div className='flex flex-col flex-1 min-h-0'>
                <div className='w-full h-full flex-1 min-h-50'>
                  <ComparisonChart
                    data={comparisonData}
                    decimalPlaces={decimalPlaces}
                    showSecondary={!!secondaryWidgetType}
                    primaryLegendLabel={primaryWidgetConfig?.title}
                    secondaryLegendLabel={secondaryWidgetConfig?.title}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className='flex flex-col h-full min-h-0 p-3 rounded-lg'>
              <div className='flex flex-col flex-1 min-h-0'>
                <EmptyStateSkeleton title='No data to display' className='h-full' />
              </div>
            </div>
          )}
        </Loading>
      </>
    )
  }
)
